import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  QrCode, Envelope, Microphone, Lightning, FilmStrip, DeviceMobile, Camera,
  BookOpen, Star, CheckCircle, ArrowRight, Sparkle
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Pricing tab — every product listed individually with the matching a-la-carte
 * price (aligned with backend `FUNNEL_PRODUCTS` and `CONVERSION_PACKAGES`).
 *
 * Click routing:
 *   Bundles        → /checkout?package={id}  (uses CONVERSION_PACKAGES price)
 *   Individuals    → /checkout?add={id}      (PreCheckout auto-bundles Capture base)
 *
 * Naming is kept identical to what the checkout page and backend emit so the
 * line items, totals, and receipts match verbatim.
 */

// ── BUNDLES (priced to match backend CONVERSION_PACKAGES) ──
// `includes` is the backend product list used to compute à-la-carte savings.
const BUNDLES = [
  {
    id: 'capture',       // → /checkout?package=capture   (£39, products: capture + highlight)
    name: 'The Capture',
    price: 39,
    includes: ['capture', 'highlight'],
    icon: <QrCode size={24} weight="bold" />,
    color: '#C29D52',
    desc: 'Starter bundle — QR Photo Upload + Highlight Reel.',
    highlights: ['QR Photo Upload', 'Highlight Reel', 'Live gallery'],
  },
  {
    id: 'experience',    // → /checkout?package=experience (£129)
    name: 'The Experience',
    price: 129,
    includes: ['capture', 'pocket_booth', 'guestbook', 'challenges', 'highlight', 'film'],
    badge: 'Most popular',
    badgeStyle: 'gradient',   // upgraded pill — matches /prom/<slug>/ready CTA badge
    icon: <Star size={24} weight="bold" />,
    color: '#B89762',
    desc: 'Capture + Pocket Booth + Video Guestbook + Challenges + Highlight + Event Film.',
    highlights: ['Everything in Capture', 'Pocket Booth + Guestbook', 'Event Film'],
  },
  {
    id: 'legacy',        // → /checkout?package=legacy (£199)
    name: 'The Legacy',
    price: 199,
    includes: ['capture', 'pocket_booth', 'guestbook', 'challenges', 'highlight', 'film', 'booth', 'invites', 'pro_upgrade', 'memory_book'],
    badge: 'Best value',
    badgeStyle: 'gradient',
    icon: <CheckCircle size={24} weight="bold" />,
    color: '#FFCC00',
    desc: 'Everything in Experience + Photo Booth + Invites + Memory Guest Book + Pro Upgrade.',
    highlights: ['Everything in Experience', 'Photo Booth + Invites', 'Memory Guest Book', 'Pro (no watermark)'],
  },
];

// ── INDIVIDUAL PRODUCTS (priced to match backend FUNNEL_PRODUCTS) ──
const PRODUCTS = [
  // Core
  {
    id: 'capture',
    name: 'QR Photo Upload',
    price: 29,
    category: 'Core',
    icon: <QrCode size={24} weight="bold" />,
    color: '#C29D52',
    desc: 'One QR code → every guest photo & video in one private gallery.',
    highlights: ['Live gallery', 'Unlimited guests', 'HD uploads'],
  },
  // Pre-event
  {
    id: 'invites',
    name: 'Invites',
    price: 29,
    category: 'Pre-event',
    icon: <Envelope size={24} weight="bold" />,
    color: '#B89762',
    desc: 'Send branded invites and track RSVPs automatically.',
    highlights: ['Custom design', 'Auto-reminders', 'RSVP dashboard'],
  },
  {
    id: 'guestbook',
    name: 'Video Guestbook',
    price: 39,
    category: 'Pre-event',
    icon: <Microphone size={24} weight="bold" />,
    color: '#26A69A',
    desc: 'Audio & video messages from every guest — keep forever.',
    highlights: ['Voice + video', 'Unlimited entries', 'Download all'],
  },
  {
    id: 'challenges',
    name: 'Challenge Mode',
    price: 29,
    category: 'Pre-event',
    icon: <Lightning size={24} weight="bold" />,
    color: '#B89762',
    desc: 'Live challenges, leaderboards and party effects.',
    highlights: ['40+ challenges', 'Live leaderboard', 'Auto rewards'],
  },
  // On-site
  {
    id: 'booth',
    name: 'Photo Booth',
    price: 79,
    category: 'On-site',
    icon: <Camera size={24} weight="bold" />,
    color: '#C29D52',
    desc: 'Turn any device into an event photobooth with templates.',
    highlights: ['iPad/Mac ready', '7 templates', 'Instant prints'],
  },
  {
    id: 'pocket_booth',
    name: 'Pocket Booth',
    price: 49,
    category: 'On-site',
    icon: <DeviceMobile size={24} weight="bold" />,
    color: '#FF6B6B',
    desc: 'Guests run the booth on their own phone — AR masks included.',
    highlights: ['AR face masks', 'Sync mode', 'Live gallery'],
  },
  // Post-event
  {
    id: 'film',
    name: 'Event Film',
    price: 49,
    category: 'Post-event',
    icon: <FilmStrip size={24} weight="bold" />,
    color: '#A033D8',
    desc: 'All content → one cinematic film. Auto-soundtracked.',
    highlights: ['Custom music', 'HD export', 'Regenerate any time'],
  },
  {
    id: 'highlight',
    name: 'Highlight Reel',
    price: 12,
    category: 'Post-event',
    icon: <DeviceMobile size={24} weight="bold" />,
    color: '#A855F7',
    desc: '30s vertical cut for Instagram & TikTok.',
    highlights: ['9:16 vertical', 'Social-ready', 'Viral music'],
  },
  {
    id: 'memory_book',
    name: 'Memory Guest Book',
    price: 29,
    category: 'Post-event',
    badge: 'New',
    icon: <BookOpen size={24} weight="bold" />,
    color: '#C29D52',
    desc: 'A5 keepsake PDF: cover, guest messages, photo gallery. Arrange print anywhere.',
    highlights: ['A5 print-ready', 'Guest signatures', 'Live flipbook'],
  },
  // Add-on
  {
    id: 'pro_upgrade',
    name: 'Pro Upgrade',
    price: 9.99,
    category: 'Add-on',
    icon: <Star size={24} weight="bold" />,
    color: '#FFCC00',
    desc: 'Remove EventDrop watermark + unlock premium music library.',
    highlights: ['No watermark', 'Premium music', 'Priority render'],
  },
];

const CATEGORY_ORDER = ['Core', 'Pre-event', 'On-site', 'Post-event', 'Add-on'];

// À-la-carte prices used to calculate bundle savings — mirrors backend
// `FUNNEL_PRODUCTS`. Kept in sync with the PRODUCTS array above.
const A_LA_CARTE = {
  capture: 29, highlight: 12, pocket_booth: 49, guestbook: 39,
  challenges: 29, film: 49, booth: 79, invites: 29, pro_upgrade: 9.99,
  memory_book: 29,
};

const computeSavings = (bundle) => {
  if (!bundle.includes?.length) return null;
  const alaCarte = bundle.includes.reduce((s, id) => s + (A_LA_CARTE[id] || 0), 0);
  const save = Math.max(0, alaCarte - bundle.price);
  const pct = alaCarte > 0 ? Math.round((save / alaCarte) * 100) : 0;
  // Only show the savings line if it's a meaningful number.
  if (save < 1 || pct < 3) return null;
  return { alaCarte, save, pct };
};

const priceLabel = (p) => Number.isInteger(p) ? `${p}` : p.toFixed(2);

const ProductCard = ({ p, onBuy, testidPrefix }) => {
  const savings = p.includes ? computeSavings(p) : null;
  return (
  <div
    className="group rounded-2xl p-5 border border-white/10 bg-[#1B1C22] hover:border-white/30 transition-all flex flex-col relative"
    data-testid={`${testidPrefix}-${p.id}`}
  >
    {p.seatsChip && (
      <div
        className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-1 rounded-full border"
        style={{
          background: 'rgba(255,138,0,0.14)',
          borderColor: 'rgba(255,138,0,0.45)',
          animation: 'seatsChipPulse 2.2s ease-in-out infinite',
        }}
        data-testid={`${testidPrefix}-${p.id}-seats-chip`}
      >
        <span
          className="w-1.5 h-1.5 rounded-full"
          style={{
            background: '#FF8A00',
            boxShadow: '0 0 6px rgba(255,138,0,0.9)',
            animation: 'seatsDotPulse 1.6s ease-in-out infinite',
          }}
        />
        <span className="text-[9px] font-black uppercase tracking-wider text-orange-300">
          {p.seatsChip.remaining}/{p.seatsChip.total} left
        </span>
      </div>
    )}
    <div className="flex items-start gap-3 mb-3">
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: `${p.color}20`, color: p.color }}
      >
        {p.icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h4 className="text-white font-bold text-base truncate">{p.name}</h4>
          {p.badge && (
            p.badgeStyle === 'gradient' ? (
              <span
                className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full text-white shadow-sm"
                style={{ background: 'linear-gradient(135deg, #F59E0B, #EF4444)' }}
                data-testid={`${testidPrefix}-${p.id}-badge`}
              >
                <Sparkle size={9} weight="fill" /> {p.badge}
              </span>
            ) : (
              <span
                className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full"
                style={{ background: `${p.color}20`, color: p.color }}
                data-testid={`${testidPrefix}-${p.id}-badge`}
              >
                {p.badge}
              </span>
            )
          )}
        </div>
        <p className="text-gray-400 text-xs mt-0.5 leading-snug">{p.desc}</p>
      </div>
    </div>

    {p.highlights && (
      <ul className="mb-4 space-y-1 text-[11px] text-gray-400">
        {p.highlights.map(h => (
          <li key={h} className="flex items-center gap-1.5">
            <CheckCircle size={12} weight="bold" style={{ color: p.color }} /> {h}
          </li>
        ))}
      </ul>
    )}

    {/* Challenge Mode ⟷ Mega Missions disambiguator — addresses the most
        common pre-purchase question: "Do I need both? What's the diff?" */}
    {p.id === 'challenges' && (
      <details
        className="mb-4 rounded-xl bg-white/[0.03] border border-white/10 group"
        data-testid={`${testidPrefix}-${p.id}-vs-missions`}
      >
        <summary
          className="flex items-center justify-between cursor-pointer px-3 py-2.5 text-[11px] font-bold text-white/70 hover:text-white transition-colors select-none list-none"
        >
          <span className="flex items-center gap-1.5">
            <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor" style={{ color: p.color }}>
              <path d="M10 0a10 10 0 100 20 10 10 0 000-20zm0 4a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm2 12H8v-1h1V9H8V8h3v7h1v1z"/>
            </svg>
            How is this different from Mega Missions?
          </span>
          <span className="text-white/30 group-open:rotate-180 transition-transform text-xs">⌄</span>
        </summary>
        <div className="px-3 pb-3 pt-1 text-[11px] text-gray-400 leading-relaxed space-y-2">
          <p>
            <b className="text-white/80">Challenge Mode</b> is the event's <i>shot list</i> — you (the host) schedule creative prompts at specific moments (“Group selfie at the toast”, “Dance-floor moment at 9:30”) and guests upload to each one.
          </p>
          <p>
            <b className="text-white/80">Mega Missions</b> is a gamified layer on top — guests compete for 🥉/🥈/🥇 badges by using every feature you bought (Photobooth, Voice Guestbook, Challenges, etc). Timers, leaderboards, squads, Golden Hour boost, end-of-night Squad Crown broadcast.
          </p>
          <p className="pt-1 border-t border-white/5">
            <span className="text-emerald-300 font-bold">They stack:</span> one mission is literally <i>"Complete 3 Challenge Mode prompts"</i>. More prompts finished → more guest-captured content for you.
          </p>
          <p className="text-white/40 italic">
            Mega Missions is included free with <b>Experience</b> &amp; <b>Legacy</b> packages — no need to buy Challenge Mode separately on those tiers.
          </p>
        </div>
      </details>
    )}

    {savings && (
      <div
        className="mb-3 text-[10px] font-bold uppercase tracking-wider rounded-lg px-2 py-1.5 inline-flex items-center gap-1.5 self-start"
        style={{ background: `${p.color}20`, color: p.color }}
        data-testid={`${testidPrefix}-${p.id}-savings`}
      >
        Worth &pound;{priceLabel(savings.alaCarte)} — save &pound;{priceLabel(savings.save)} ({savings.pct}% off)
      </div>
    )}

    <div className="mt-auto flex items-center justify-between gap-2">
      <div className="flex items-baseline gap-2">
        <span className="text-white font-black text-2xl">&pound;{priceLabel(p.price)}</span>
        {p.wasPrice && p.wasPrice > p.price && (
          <span className="line-through text-white/40 text-sm" data-testid={`${testidPrefix}-${p.id}-was-price`}>
            &pound;{priceLabel(p.wasPrice)}
          </span>
        )}
        <span className="text-gray-500 text-[10px]">one-off</span>
      </div>
      <button
        type="button"
        onClick={() => onBuy(p)}
        className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl text-sm font-bold text-white transition-all hover:scale-[1.02] active:scale-[0.97]"
        style={{ background: `linear-gradient(135deg, ${p.color}, #B89762)` }}
        data-testid={`pricing-buy-${p.id}`}
      >
        Buy now <ArrowRight size={14} weight="bold" />
      </button>
    </div>
  </div>
  );
};

const PricingTab = () => {
  const navigate = useNavigate();
  const [founder, setFounder] = useState(null);

  useEffect(() => {
    fetch(`${API}/pricing/legacy-founder`).then(r => r.ok ? r.json() : null)
      .then(setFounder).catch(() => {});
  }, []);

  const buyBundle = (bundle) => navigate(`/checkout?package=${encodeURIComponent(bundle.id)}`);
  // Individual add-ons go through the legacy PreCheckout route which understands
  // `?add=X` and auto-bundles the Capture base, showing an itemised subtotal.
  const buyAddon = (product) => navigate(`/checkout-old?add=${encodeURIComponent(product.id)}`);

  // Apply the live Legacy launch pricing: £199 launch, was £249 — only N of 50 seats.
  // Once seats run out, the founder status becomes inactive and Legacy shows £249 only.
  const bundles = BUNDLES.map(b => {
    if (b.id !== 'legacy') return b;
    if (!founder?.active) return { ...b, price: founder?.standard_price ?? b.price };
    return {
      ...b,
      price: founder.founder_price,
      wasPrice: founder.standard_price,
      seatsChip: { remaining: founder.seats_remaining, total: founder.seats_total },
    };
  });

  const bestPct = Math.max(0, ...bundles.map(b => computeSavings(b)?.pct || 0));

  const byCategory = CATEGORY_ORDER.map(cat => ({
    cat,
    items: PRODUCTS.filter(p => p.category === cat),
  })).filter(g => g.items.length > 0);

  return (
    <div data-testid="pricing-tab">
      <style>{`
        @keyframes seatsChipPulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255,138,0,0.35); }
          50%      { transform: scale(1.04); box-shadow: 0 0 0 6px rgba(255,138,0,0); }
        }
        @keyframes seatsDotPulse {
          0%, 100% { opacity: 1;   }
          50%      { opacity: 0.45; }
        }
      `}</style>
      <div className="mb-6 flex items-start gap-3 rounded-2xl border border-[#B89762]/50/20 bg-[#B89762]/5 px-4 py-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #C29D52, #B89762)' }}
        >
          <CheckCircle size={18} weight="bold" className="text-white" />
        </div>
        <div className="flex-1">
          <p className="text-white text-sm font-bold">Buy once · Use forever · No subscription</p>
          <p className="text-gray-400 text-xs mt-0.5">
            Tap <span className="text-white font-semibold">Buy now</span> — we'll drop you straight into secure checkout.
          </p>
        </div>
      </div>

      {/* BUNDLES */}
      <div className="mb-8" data-testid="pricing-group-bundles">
        {founder?.active && (
          <div
            className="mb-3 rounded-2xl p-4 flex items-center gap-3"
            style={{ background: 'linear-gradient(135deg, rgba(255,59,31,0.15), rgba(255,138,0,0.08))', border: '1px solid rgba(255,138,0,0.4)' }}
            data-testid="legacy-founder-banner"
          >
            <div className="text-2xl leading-none flex-shrink-0 animate-pulse">🔥</div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-black uppercase tracking-[0.15em] text-white/60">Launch Offer · Limited Seats</p>
              <p className="text-sm text-white font-bold leading-snug mt-0.5">
                The Legacy for <span className="text-orange-400">&pound;{founder.founder_price}</span>, was{' '}
                <span className="line-through text-white/40 text-xs">&pound;{founder.standard_price}</span> — only{' '}
                <span className="text-orange-400 font-black">{founder.seats_remaining}</span> of {founder.seats_total} seats left
              </p>
            </div>
          </div>
        )}

        <div className="flex items-end justify-between flex-wrap gap-2 mb-3">
          <h3 className="text-white/60 text-xs font-bold uppercase tracking-[0.18em] flex items-center gap-2">
            <Sparkle size={12} weight="bold" style={{ color: '#FFCC00' }} /> Bundles
          </h3>
          <p className="text-[10px] text-white/50">
            <span className="text-[#FFCC00] font-bold">Save up to {bestPct}%</span> vs à la carte
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          {bundles.map(b => <ProductCard key={b.id} p={b} onBuy={buyBundle} testidPrefix="pricing-bundle" />)}
        </div>
      </div>

      {/* INDIVIDUAL PRODUCTS */}
      {byCategory.map(({ cat, items }) => (
        <div key={cat} className="mb-8" data-testid={`pricing-group-${cat.toLowerCase().replace(/\s+/g, '-')}`}>
          <h3 className="text-white/60 text-xs font-bold uppercase tracking-[0.18em] mb-3">{cat}</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {items.map(p => <ProductCard key={p.id} p={p} onBuy={buyAddon} testidPrefix="pricing-card" />)}
          </div>
        </div>
      ))}

      <p className="text-gray-500 text-[11px] text-center mt-4">
        Secure checkout by Stripe · VAT included · Powered by eventdrop.co
      </p>
    </div>
  );
};

export default PricingTab;
